import React, { Fragment, useRef } from "react";
import { wire } from "react-hot-wire";
import MaterialTable, { MTableToolbar, Query } from "material-table";
import { WithTranslation, withTranslation } from "react-i18next";

import IconEdit from "@material-ui/icons/Edit";
import IconDelete from "@material-ui/icons/Delete";

import { PrizeSupplierService } from "services/prize/supplier/PrizeSupplier.service";
import { DrawerService } from "services/drawer/Drawer.service";

import { PrizeSupplier } from "models/prize/supplier/PrizeSupplier";
import Table, { Column, ColumnDefinition } from "models/common/Table";

import Menu from "components/common/menu/Menu";

function PrizeSupplierList({
  prizeSupplierService,
  drawerService,
  t
}: {
  prizeSupplierService: PrizeSupplierService;
  drawerService: DrawerService;
} & WithTranslation) {
  const tableRef = useRef<any>(null);
  const refresh = () => tableRef.current.onQueryChange();

  return (
    <Fragment>
      <div className="m-3">
        <MaterialTable
          tableRef={tableRef}
          title={t("prize.supplier.list.title")}
          options={{ ...Table.options, loadingType: "overlay" }}
          columns={[
            ...([
              {
                title: t("prize.supplier.list.column.id"),
                field: "id",
                defaultSort: "desc"
              },
              {
                title: t("prize.supplier.list.column.lastUpdate"),
                field: "lastUpdate"
              },
              {
                title: t("prize.supplier.list.column.name"),
                field: "type",
                customSort: (a, b) => a.type.localeCompare(b.type),
                render: supplier => (
                  <a
                    href="/"
                    onClick={event => {
                      event.preventDefault();

                      drawerService.activate("edit-prize-supplier", {
                        prizeSupplier: supplier,
                        action: refresh
                      });
                    }}
                  >
                    {supplier.type}
                  </a>
                )
              },
              {
                title: t("prize.supplier.list.column.priceMargin"),
                field: "priceMargin",
                cellStyle: { width: "100%" },
                render: supplier => `${supplier.priceMargin}%`
              },
              {
                title: t("prize.supplier.list.column.actions"),
                sorting: false,
                editable: "never",
                render: prizeSupplier =>
                  prizeSupplier && (
                    <Menu
                      translationPath="prize.supplier.list.rowMenu"
                      items={[
                        {
                          icon: <IconEdit fontSize="small" className="mr-2" />,
                          text: "edit",
                          onClick: () =>
                            drawerService.activate("edit-prize-supplier", {
                              prizeSupplier,
                              action: refresh
                            })
                        },
                        {
                          icon: (
                            <IconDelete fontSize="small" className="mr-2" />
                          ),
                          text: "delete",
                          disabled: true
                        }
                      ]}
                    />
                  )
              }
            ] as ColumnDefinition<PrizeSupplier>[]).map(
              column => new Column<PrizeSupplier>(column)
            )
          ]}
          data={async (query: Query<PrizeSupplier>) =>
            await prizeSupplierService.list(query)
          }
          components={{
            Toolbar: props => (
              <MTableToolbar {...props} search={!props.selectedRows.length} />
            )
          }}
          actions={[
            {
              icon: "delete",
              tooltip: t("prize.supplier.list.actions.delete.tooltip"),
              onClick: () =>
                window.confirm(
                  t("prize.supplier.list.actions.delete.confirmation")
                )
            }
          ]}
          localization={t("prize.supplier.list.localization", {
            returnObjects: true
          })}
        />
      </div>
    </Fragment>
  );
}

export default wire(
  ["prizeSupplierService", "drawerService"],
  withTranslation()(PrizeSupplierList)
);
